/**
 * API Tokens Service
 *
 * Handles all API requests for token management.
 */

// В Docker используется относительный путь через proxy
// Внутри docker-compose: frontend (3000) -> proxy -> backend (8000)
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Fetch all API tokens
 *
 * @param {number} skip - Number of tokens to skip
 * @param {number} limit - Maximum tokens to return
 * @param {boolean|null} isActive - Filter by active status
 * @returns {Promise<Object>} Response with tokens array and pagination info
 */
export const fetchTokens = async (skip = 0, limit = 100, isActive = null) => {
  const params = new URLSearchParams({
    skip: skip.toString(),
    limit: limit.toString()
  });

  if (isActive !== null) {
    params.append('is_active', isActive.toString());
  }

  const response = await fetch(`${API_BASE_URL}/tokens?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch tokens');
  }

  return response.json();
};

/**
 * Fetch single token details
 *
 * @param {number} tokenId - Token ID
 * @returns {Promise<Object>} Token details
 */
export const fetchTokenById = async (tokenId) => {
  const response = await fetch(`${API_BASE_URL}/tokens/${tokenId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch token');
  }

  return response.json();
};

/**
 * Create new API token
 *
 * @param {Object} tokenData - Token creation data
 * @param {string} tokenData.name - Token name
 * @param {Array<string>} tokenData.scopes - Array of permission scopes
 * @param {string|null} tokenData.expires_at - ISO 8601 expiration date or null
 * @returns {Promise<Object>} Created token with plaintext token (shown once!)
 */
export const createToken = async (tokenData) => {
  const response = await fetch(`${API_BASE_URL}/tokens`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(tokenData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create token');
  }

  return response.json();
};

/**
 * Update token name
 *
 * @param {number} tokenId - Token ID
 * @param {string} name - New token name
 * @returns {Promise<Object>} Updated token
 */
export const updateToken = async (tokenId, name) => {
  const response = await fetch(`${API_BASE_URL}/tokens/${tokenId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ name })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update token');
  }

  return response.json();
};

/**
 * Revoke (deactivate) token
 *
 * @param {number} tokenId - Token ID
 * @returns {Promise<Object>} Success response
 */
export const revokeToken = async (tokenId) => {
  const response = await fetch(`${API_BASE_URL}/tokens/${tokenId}/revoke`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to revoke token');
  }

  return response.json();
};

/**
 * Delete token permanently
 *
 * @param {number} tokenId - Token ID
 * @returns {Promise<Object>} Success response
 */
export const deleteToken = async (tokenId) => {
  const response = await fetch(`${API_BASE_URL}/tokens/${tokenId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete token');
  }

  return response.json();
};

/**
 * Fetch tokens expiring soon
 *
 * @param {number} days - Days until expiration to look ahead
 * @returns {Promise<Object>} Response with expiring tokens
 */
export const fetchExpiringTokens = async (days = 7) => {
  const response = await fetch(`${API_BASE_URL}/tokens/expiring/list?days=${days}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch expiring tokens');
  }

  return response.json();
};

/**
 * Fetch available scopes and groups
 *
 * @returns {Promise<Object>} Response with scopes and groups
 */
export const fetchAvailableScopes = async () => {
  const response = await fetch(`${API_BASE_URL}/tokens/scopes/available`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch available scopes');
  }

  return response.json();
};
